import type { SimulationNodeDatum } from "d3-force";

export interface LayoutEdge {
  source: string;
  target: string;
}

export interface LayoutPoint {
  x: number;
  y: number;
}

interface PlacedComponent extends LayoutPoint {
  radius: number;
}

const TAU = Math.PI * 2;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const ORDERING_PASSES = 4;

export const buildNeighbors = (ids: string[], edges: LayoutEdge[]) => {
  const neighbors = new Map<string, Set<string>>(ids.map((id) => [id, new Set<string>()]));
  edges.forEach(({ source, target }) => {
    if (source === target) return;
    neighbors.get(source)?.add(target);
    neighbors.get(target)?.add(source);
  });
  return neighbors;
};

export const clusterRadii = (neighbors: Map<string, Set<string>>, spacing: number) => {
  const radii = new Map<string, number>();
  neighbors.forEach((adjacent, id) => {
    let share = 0;
    adjacent.forEach((neighbor) => {
      share += 1 / Math.max(1, neighbors.get(neighbor)?.size ?? 1);
    });
    radii.set(id, share > 1 ? Math.sqrt(share) * spacing * 0.75 : 0);
  });
  return radii;
};

const layoutComponent = (
  root: string,
  neighbors: Map<string, Set<string>>,
  visited: Set<string>,
  ringGap: number,
  spacing: number,
) => {
  const order = [root];
  const depth = new Map<string, number>([[root, 0]]);
  const children = new Map<string, string[]>();
  visited.add(root);
  for (let index = 0; index < order.length; index += 1) {
    const current = order[index];
    const next = [...(neighbors.get(current) ?? [])]
      .filter((neighbor) => !visited.has(neighbor))
      .sort((first, second) => (neighbors.get(second)?.size ?? 0) - (neighbors.get(first)?.size ?? 0));
    next.forEach((neighbor) => {
      visited.add(neighbor);
      depth.set(neighbor, (depth.get(current) ?? 0) + 1);
      order.push(neighbor);
    });
    children.set(current, next);
  }

  const weight = new Map<string, number>();
  for (let index = order.length - 1; index >= 0; index -= 1) {
    const id = order[index];
    const childIds = children.get(id) ?? [];
    weight.set(id, childIds.length ? childIds.reduce((total, child) => total + (weight.get(child) ?? 1), 0) : 1);
  }

  const ringCounts: number[] = [];
  order.forEach((id) => {
    const level = depth.get(id) ?? 0;
    ringCounts[level] = (ringCounts[level] ?? 0) + 1;
  });
  const ringRadii = ringCounts.map(() => 0);
  for (let level = 1; level < ringCounts.length; level += 1) {
    ringRadii[level] = Math.max(ringRadii[level - 1] + ringGap, (ringCounts[level] * spacing) / TAU);
  }

  const angles = new Map<string, number>();
  const assignAngles = () => {
    const stack: Array<{ id: string; start: number; end: number }> = [{ id: root, start: 0, end: TAU }];
    while (stack.length) {
      const { id, start, end } = stack.pop()!;
      angles.set(id, (start + end) / 2);
      const total = weight.get(id) ?? 1;
      let cursor = start;
      (children.get(id) ?? []).forEach((child) => {
        const span = ((end - start) * (weight.get(child) ?? 1)) / total;
        stack.push({ id: child, start: cursor, end: cursor + span });
        cursor += span;
      });
    }
  };

  const nearestAngle = (angle: number, reference: number) =>
    reference + Math.atan2(Math.sin(angle - reference), Math.cos(angle - reference));

  assignAngles();
  for (let pass = 0; pass < ORDERING_PASSES; pass += 1) {
    children.forEach((childIds, parent) => {
      if (childIds.length < 2) return;
      const reference = angles.get(parent) ?? 0;
      const keys = new Map(
        childIds.map((child) => {
          const own = nearestAngle(angles.get(child) ?? 0, reference);
          const others = [...(neighbors.get(child) ?? [])].filter((neighbor) => neighbor !== parent);
          if (!others.length) return [child, own];
          const pull = others.reduce((total, neighbor) => total + nearestAngle(angles.get(neighbor) ?? 0, reference), 0);
          return [child, (own + pull) / (others.length + 1)];
        }),
      );
      childIds.sort((first, second) => (keys.get(first) ?? 0) - (keys.get(second) ?? 0));
    });
    assignAngles();
  }

  const positions = new Map<string, LayoutPoint>();
  angles.forEach((angle, id) => {
    const radius = ringRadii[depth.get(id) ?? 0];
    positions.set(id, { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius });
  });

  return { positions, radius: ringRadii[ringRadii.length - 1] + spacing / 2 };
};

export const seedRadialLayout = (
  ids: string[],
  neighbors: Map<string, Set<string>>,
  center: LayoutPoint,
  ringGap: number,
  spacing: number,
) => {
  const byDegree = [...ids].sort((first, second) => (neighbors.get(second)?.size ?? 0) - (neighbors.get(first)?.size ?? 0));
  const visited = new Set<string>();
  const components: Array<{ root: string; positions: Map<string, LayoutPoint>; radius: number }> = [];
  byDegree.forEach((root) => {
    if (!visited.has(root)) components.push({ root, ...layoutComponent(root, neighbors, visited, ringGap, spacing) });
  });
  components.sort((first, second) => second.positions.size - first.positions.size);

  const positions = new Map<string, LayoutPoint>();
  const placed: PlacedComponent[] = [];
  let step = 1;
  components.forEach((component, index) => {
    let offset: LayoutPoint = { x: 0, y: 0 };
    if (index > 0) {
      const fits = (candidate: LayoutPoint) =>
        placed.every(
          (other) => Math.hypot(candidate.x - other.x, candidate.y - other.y) >= other.radius + component.radius + spacing / 2,
        );
      do {
        const distance = Math.sqrt(step) * spacing * 0.9;
        offset = { x: Math.cos(step * GOLDEN_ANGLE) * distance, y: Math.sin(step * GOLDEN_ANGLE) * distance };
        step += 1;
      } while (!fits(offset));
    }
    placed.push({ ...offset, radius: component.radius });
    component.positions.forEach((point, id) => {
      positions.set(id, { x: center.x + offset.x + point.x, y: center.y + offset.y + point.y });
    });
  });

  return { positions, root: components[0]?.root };
};

export const forceNoRotation = <T extends SimulationNodeDatum>() => {
  let nodes: T[] = [];
  const force = () => {
    if (!nodes.length || nodes.some((node) => node.fx != null || node.fy != null)) return;
    let centerX = 0;
    let centerY = 0;
    nodes.forEach((node) => {
      centerX += node.x ?? 0;
      centerY += node.y ?? 0;
    });
    centerX /= nodes.length;
    centerY /= nodes.length;
    let momentum = 0;
    let inertia = 0;
    nodes.forEach((node) => {
      const dx = (node.x ?? 0) - centerX;
      const dy = (node.y ?? 0) - centerY;
      momentum += dx * (node.vy ?? 0) - dy * (node.vx ?? 0);
      inertia += dx * dx + dy * dy;
    });
    if (!inertia) return;
    const spin = momentum / inertia;
    nodes.forEach((node) => {
      node.vx = (node.vx ?? 0) + spin * ((node.y ?? 0) - centerY);
      node.vy = (node.vy ?? 0) - spin * ((node.x ?? 0) - centerX);
    });
  };
  force.initialize = (initialNodes: T[]) => {
    nodes = initialNodes;
  };
  return force;
};

export const forceAnchor = <T extends SimulationNodeDatum & { id: string }>(id: string, target: LayoutPoint, strength = 0.2) => {
  let nodes: T[] = [];
  let anchor: T | undefined;
  const force = () => {
    if (!anchor || nodes.some((node) => node.fx != null || node.fy != null)) return;
    const shiftX = (target.x - (anchor.x ?? 0) - (anchor.vx ?? 0)) * strength;
    const shiftY = (target.y - (anchor.y ?? 0) - (anchor.vy ?? 0)) * strength;
    nodes.forEach((node) => {
      node.vx = (node.vx ?? 0) + shiftX;
      node.vy = (node.vy ?? 0) + shiftY;
    });
  };
  force.initialize = (initialNodes: T[]) => {
    nodes = initialNodes;
    anchor = initialNodes.find((node) => node.id === id);
  };
  return force;
};
